import { useEffect, useState, useRef, useCallback } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useMyContext } from "../context";
import PagesLoading from "../Components/PagesLoading";
import { decodeAndValidateToken } from "../utils/tokenUtils";

const ProtectedRoute = ({ children }) => {
  const {
    checkTokenValidity,
    isAuthenticated,
    setIsAuthenticated,
    isTokenChecked,
  } = useMyContext();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const authCheckInProgress = useRef(false);
  const mountedRef = useRef(true);

  // Debug: Monitor token changes
  useEffect(() => {
    if (import.meta.env.DEV) {
      const token = localStorage.getItem("ecoToken");
      const refreshToken = localStorage.getItem("ecoRefreshToken");

      console.log("🛡️ ProtectedRoute mounted with tokens:", {
        tokenExists: !!token,
        refreshTokenExists: !!refreshToken,
        path: location.pathname,
      });

      // Monitor for token deletion
      const interval = setInterval(() => {
        const currentToken = localStorage.getItem("ecoToken");
        const currentRefreshToken = localStorage.getItem("ecoRefreshToken");

        if (token && !currentToken) {
          console.error("🚨 CRITICAL: ecoToken was deleted!");
          console.trace("Stack trace:");
        }
        if (refreshToken && !currentRefreshToken) {
          console.error("🚨 CRITICAL: ecoRefreshToken was deleted!");
          console.trace("Stack trace:");
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [location.pathname]);

  // Simplified direct token validation
  const validateTokenDirectly = useCallback(async () => {
    try {
      const token = localStorage.getItem("ecoToken");
      const refreshToken = localStorage.getItem("ecoRefreshToken");

      if (!token) {
        console.log("❌ No token in localStorage");
        return false;
      }

      // Try to decode the token
      try {
        const { isValid, isExpired } = decodeAndValidateToken(token);

        if (isValid && !isExpired) {
          console.log("✅ Token is valid (direct validation)");

          // Update context state
          if (!isAuthenticated) {
            console.log("🔄 Updating context authentication state...");
            setIsAuthenticated(true);

            // Parse and set user data
            const userData = localStorage.getItem("userData");
            if (userData) {
              try {
                JSON.parse(userData); // Just validate JSON
              } catch (error) {
                console.error("❌ Error parsing userData:", error);
              }
            }
          }
          return true;
        }

        // Token is expired but we have refresh token
        if (isExpired && refreshToken) {
          console.log("🔄 Token expired, attempting refresh via context...");
          try {
            const refreshed = await checkTokenValidity();
            if (refreshed) {
              console.log("✅ Token refreshed successfully");
              // Update authentication state
              if (!isAuthenticated) {
                setIsAuthenticated(true);
              }
              return true;
            } else {
              console.log("⚠️ Refresh returned false, token may be invalid");
              return false;
            }
          } catch (refreshError) {
            console.error("❌ Refresh failed:", refreshError);
            // DON'T clear tokens here - return false but keep tokens
            // The token might still be valid on the server side
            return false;
          }
        }

        // Token is expired and no refresh token
        if (isExpired && !refreshToken) {
          console.log("❌ Token expired and no refresh token available");
          return false;
        }

        return false;
      } catch (decodeError) {
        console.error("❌ Error decoding token:", decodeError.message);

        // Even if decode fails, if we have a token string, check with context
        if (token && typeof token === "string" && token.length > 10) {
          console.log("⚠️ JWT decode failed, trying context validation...");
          try {
            const contextValid = await checkTokenValidity();
            return contextValid;
          } catch (contextError) {
            console.error("❌ Context validation failed:", contextError);
          }
        }
        return false;
      }
    } catch (error) {
      console.error("❌ Error in direct token validation:", error);
      return false;
    }
  }, [isAuthenticated, setIsAuthenticated, checkTokenValidity]);

  // Main authentication check - simplified and reliable
  useEffect(() => {
    // Set mounted ref
    mountedRef.current = true;

    // Skip auth check for login pages
    if (location.pathname === "/" || location.pathname === "/login") {
      setIsChecking(false);
      setIsValidToken(false);
      return;
    }

    // Wait for context to finish initial token check
    if (!isTokenChecked) {
      console.log("⏳ Waiting for context token check to complete...");
      setIsChecking(true);
      return;
    }

    // Prevent multiple simultaneous checks
    if (authCheckInProgress.current) {
      console.log("⏭️ Auth check already in progress, skipping");
      return;
    }

    // Once context has checked, verify authentication
    const performAuthCheck = async () => {
      if (!mountedRef.current) return;

      try {
        authCheckInProgress.current = true;
        setIsChecking(true);

        console.log("🛡️ Starting authentication verification for:", {
          path: location.pathname,
          isAuthenticated,
          isTokenChecked,
          hasToken: !!localStorage.getItem("ecoToken"),
          hasRefreshToken: !!localStorage.getItem("ecoRefreshToken"),
        });

        // If context says authenticated, trust it
        if (isAuthenticated) {
          console.log("✅ Already authenticated via context");
          if (mountedRef.current) {
            setIsValidToken(true);
            setIsChecking(false);
          }
          return;
        }

        // Context says not authenticated, but tokens exist - do direct validation
        const token = localStorage.getItem("ecoToken");
        const refreshToken = localStorage.getItem("ecoRefreshToken");

        if (!token && !refreshToken) {
          // No tokens at all, definitely not authenticated
          console.log("❌ No tokens found, user not authenticated");
          if (mountedRef.current) {
            setIsValidToken(false);
            setIsChecking(false);
          }
          return;
        }

        // Tokens exist but context says not authenticated - verify directly
        console.log(
          "⚠️ Context says not authenticated, but tokens exist. Verifying..."
        );
        const isValid = await validateTokenDirectly();

        if (mountedRef.current) {
          setIsValidToken(isValid);
          setIsChecking(false);
        }

        console.log("🛡️ Final verification result:", {
          isValidToken: isValid,
          path: location.pathname,
          hasToken: !!localStorage.getItem("ecoToken"),
          hasRefreshToken: !!localStorage.getItem("ecoRefreshToken"),
          isAuthenticated,
        });
      } catch (error) {
        console.error("❌ Error in auth verification:", error);
        if (mountedRef.current) {
          setIsValidToken(false);
          setIsChecking(false);
        }

        // CRITICAL: NEVER clear tokens here
        console.log("⚠️ Auth check failed - KEEPING tokens in localStorage:", {
          token: !!localStorage.getItem("ecoToken"),
          refreshToken: !!localStorage.getItem("ecoRefreshToken"),
        });
      } finally {
        authCheckInProgress.current = false;
      }
    };

    // Small delay to ensure state is stable
    const timer = setTimeout(performAuthCheck, 50);

    return () => {
      mountedRef.current = false;
      clearTimeout(timer);
      if (authCheckInProgress.current) {
        console.log("⚠️ Component unmounting during auth check");
        authCheckInProgress.current = false;
      }
    };
  }, [
    location.pathname,
    isTokenChecked,
    isAuthenticated,
    validateTokenDirectly,
  ]);

  // Sync isValidToken with isAuthenticated when context state changes
  useEffect(() => {
    // Only sync if context has checked and we're not on login page
    if (
      isTokenChecked &&
      location.pathname !== "/" &&
      location.pathname !== "/login"
    ) {
      if (isAuthenticated && !isValidToken) {
        console.log(
          "🔄 Context authentication state changed to true, updating..."
        );
        setIsValidToken(true);
        setIsChecking(false);
      } else if (!isAuthenticated && isValidToken) {
        console.log(
          "🔄 Context authentication state changed to false, re-checking..."
        );
        // Re-trigger auth check
        setIsValidToken(false);
        setIsChecking(true);
      }
    }
  }, [isAuthenticated, isTokenChecked, isValidToken, location.pathname]);

  // Show loading while checking
  if (isChecking) {
    console.log("⏳ Auth check in progress for:", location.pathname);
    return <PagesLoading />;
  }

  // Check if we should redirect to login
  if (!isValidToken) {
    console.log("🔒 Authentication failed, redirecting to login");
    console.log("📊 Final state:", {
      isValidToken,
      hasToken: !!localStorage.getItem("ecoToken"),
      hasRefreshToken: !!localStorage.getItem("ecoRefreshToken"),
      path: location.pathname,
      isAuthenticated,
      isTokenChecked,
    });

    // Check if tokens still exist (they should!)
    const tokenStillExists = !!localStorage.getItem("ecoToken");
    const refreshTokenStillExists = !!localStorage.getItem("ecoRefreshToken");

    if (!tokenStillExists) {
      console.error("🚨 WARNING: Token was deleted during auth check!");
    }
    if (!refreshTokenStillExists) {
      console.error("🚨 WARNING: Refresh token was deleted during auth check!");
    }

    // Keep the current path for redirect back
    const returnPath =
      location.pathname !== "/" && location.pathname !== "/login"
        ? location.pathname + location.search
        : "/dashboard";

    return (
      <Navigate
        to="/"
        replace
        state={{
          from: returnPath,
          message: "Please login to continue",
          timestamp: Date.now(),
          wasAuthenticated: isAuthenticated,
          tokenDeleted: !tokenStillExists,
          refreshTokenDeleted: !refreshTokenStillExists,
        }}
      />
    );
  }

  // Authentication successful
  console.log("✅ Authentication successful for:", location.pathname);
  return children;
};

export default ProtectedRoute;
